<template>
    <div class="max-w-7xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
            Add Payment Method
          </h1>
          <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Add a new credit or debit card to your account
          </p>
        </div>
        <Link :href="route('settings.billing.payment-methods.index')">
          <Button variant="secondary">Cancel</Button>
        </Link>
      </div>

      <!-- Payment Method Form -->
      <Card>
        <form @submit.prevent="handleSubmit" class="space-y-6">
          <!-- Cardholder Name -->
          <div>
            <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
              Cardholder Name
              <span class="text-red-500">*</span>
            </label>
            <input
              id="name"
              v-model="form.name"
              type="text"
              required
              :disabled="processing"
              class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              :class="{
                'border-red-300 dark:border-red-600': form.errors.name,
                'border-gray-300 dark:border-gray-700': !form.errors.name,
              }"
              placeholder="John Doe"
            />
            <p v-if="form.errors.name" class="mt-1 text-sm text-red-600 dark:text-red-400">
              {{ form.errors.name }}
            </p>
          </div>

          <!-- Stripe Card Element -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
              Card Information
              <span class="text-red-500">*</span>
            </label>
            <div
              ref="cardElement"
              class="border rounded-lg p-3 bg-white dark:bg-gray-800 transition-colors"
              :class="{
                'border-red-300 dark:border-red-600': cardError,
                'border-gray-300 dark:border-gray-700': !cardError,
                'opacity-50 cursor-not-allowed': processing,
              }"
            ></div>
            <p v-if="cardError" class="mt-1 text-sm text-red-600 dark:text-red-400">
              {{ cardError }}
            </p>
            <p v-else class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Your card information is handled securely by Stripe
            </p>
          </div>

          <!-- Billing Zip Code -->
          <div>
            <label for="zip" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
              Billing ZIP / Postal Code
              <span class="text-red-500">*</span>
            </label>
            <input
              id="zip"
              v-model="form.zip"
              type="text"
              required
              :disabled="processing"
              class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              :class="{
                'border-red-300 dark:border-red-600': form.errors.zip,
                'border-gray-300 dark:border-gray-700': !form.errors.zip,
              }"
              placeholder="12345"
              maxlength="10"
            />
            <p v-if="form.errors.zip" class="mt-1 text-sm text-red-600 dark:text-red-400">
              {{ form.errors.zip }}
            </p>
          </div>

          <!-- Set as Default Checkbox -->
          <div class="flex items-start">
            <div class="flex items-center h-5">
              <input
                id="set_default"
                v-model="form.set_default"
                type="checkbox"
                :disabled="processing"
                class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            <div class="ml-3">
              <label for="set_default" class="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">
                Set as default payment method
              </label>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                This card will be used for automatic payments
              </p>
            </div>
          </div>

          <!-- Security Notice -->
          <Alert variant="info">
            <template #title>
              <div class="flex items-center">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Secure Payment
              </div>
            </template>
            Your payment information is encrypted and securely transmitted to Stripe.
            We never store your full card number on our servers.
          </Alert>

          <!-- Form Actions -->
          <div class="flex items-center justify-end space-x-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Link :href="route('settings.billing.payment-methods.index')">
              <Button
                type="button"
                variant="secondary"
                :disabled="processing"
              >
                Cancel
              </Button>
            </Link>
            <Button
              type="submit"
              :loading="processing"
              :disabled="!isFormValid || processing"
            >
              {{ processing ? 'Processing...' : 'Add Payment Method' }}
            </Button>
          </div>
        </form>
      </Card>

      <!-- Stripe Badge -->
      <div class="flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
        <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
          <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
        </svg>
        Powered by Stripe
      </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useForm, router, Link } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Alert from '@/Components/Common/Alert.vue';
import { loadStripe } from '@stripe/stripe-js';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';

// Props
const props = defineProps({
  clientSecret: {
    type: String,
    required: true,
  },
  stripeKey: {
    type: String,
    required: true,
  },
  company: {
    type: Object,
    required: true,
  },
});

// Composables
const { success, error } = useToast();

// Refs
const cardElement = ref(null);
const cardError = ref(null);
const cardComplete = ref(false);
const processing = ref(false);
const stripeLoaded = ref(false);

// Stripe instances
let stripe = null;
let elements = null;
let card = null;

// Form
const form = useForm({
  name: '',
  zip: '',
  set_default: true,
  payment_method_id: null,
  _company_id: props.company.id, // Required for company context validation
});

// Computed
const isFormValid = computed(() => {
  return (
    form.name.trim() !== '' &&
    form.zip.trim() !== '' &&
    cardComplete.value &&
    !cardError.value &&
    stripeLoaded.value
  );
});

// Initialize Stripe Elements
onMounted(async () => {
  try {
    // Load Stripe.js
    stripe = await loadStripe(props.stripeKey);

    if (!stripe) {
      throw new Error('Failed to load Stripe');
    }

    // Create card element options
    const appearance = {
      theme: 'stripe',
      variables: {
        colorPrimary: '#3b82f6',
        colorBackground: document.documentElement.classList.contains('dark') ? '#1f2937' : '#ffffff',
        colorText: document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#1f2937',
        colorDanger: '#ef4444',
        fontFamily: '"Inter", sans-serif',
        spacingUnit: '4px',
        borderRadius: '8px',
      },
    };

    // Create card element
    const cardElementOptions = {
      style: {
        base: {
          fontSize: '16px',
          color: document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#1f2937',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontSmoothing: 'antialiased',
          '::placeholder': {
            color: document.documentElement.classList.contains('dark') ? '#6b7280' : '#9ca3af',
          },
        },
        invalid: {
          color: '#ef4444',
          iconColor: '#ef4444',
        },
      },
      hidePostalCode: true,
    };

    // Create elements instance
    elements = stripe.elements();

    // Create and mount card element
    card = elements.create('card', cardElementOptions);
    card.mount(cardElement.value);

    stripeLoaded.value = true;

    // Listen for card element changes
    card.on('change', (event) => {
      cardError.value = event.error ? event.error.message : null;
      cardComplete.value = event.complete;
    });

    // Listen for focus/blur events for better UX
    card.on('focus', () => {
      cardElement.value?.classList.add('ring-2', 'ring-blue-500');
    });

    card.on('blur', () => {
      cardElement.value?.classList.remove('ring-2', 'ring-blue-500');
    });
  } catch (err) {
    console.error('Error initializing Stripe:', err);
    error('Failed to initialize payment form. Please refresh the page and try again.');
  }
});

// Cleanup on unmount
onBeforeUnmount(() => {
  if (card) {
    card.destroy();
  }
});

// Handle form submission
const handleSubmit = async () => {
  // Validation
  if (!form.name.trim()) {
    error('Please enter the cardholder name');
    return;
  }

  if (!form.zip.trim()) {
    error('Please enter the billing ZIP code');
    return;
  }

  if (!cardComplete.value) {
    error('Please enter valid card details');
    return;
  }

  processing.value = true;
  cardError.value = null;

  try {
    // Confirm setup with Stripe
    const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(
      props.clientSecret,
      {
        payment_method: {
          card: card,
          billing_details: {
            name: form.name.trim(),
            address: {
              postal_code: form.zip.trim(),
            },
          },
        },
      }
    );

    // Handle Stripe errors
    if (stripeError) {
      console.error('Stripe error:', stripeError);
      cardError.value = stripeError.message;
      error(stripeError.message);
      processing.value = false;
      return;
    }

    // Verify setup intent was successful
    if (setupIntent.status !== 'succeeded') {
      error('Payment setup failed. Please try again.');
      processing.value = false;
      return;
    }

    // Send payment method ID to backend
    form.payment_method_id = setupIntent.payment_method;

    form.post(route('settings.billing.payment-methods.store'), {
      preserveScroll: true,
      onSuccess: () => {
        success('Payment method added successfully');
        // Form will redirect via Inertia
      },
      onError: (errors) => {
        console.error('Backend errors:', errors);
        const errorMessage = errors.payment_method_id || errors.message || 'Failed to save payment method';
        error(errorMessage);
      },
      onFinish: () => {
        processing.value = false;
      },
    });
  } catch (err) {
    console.error('Unexpected error:', err);
    error('An unexpected error occurred. Please try again.');
    processing.value = false;
  }
};
</script>

<style scoped>
/* Custom styles for Stripe Element container */
#card-element {
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

/* Add focus ring animation */
.ring-2 {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.5);
}

/* Dark mode adjustments */
@media (prefers-color-scheme: dark) {
  .StripeElement {
    background-color: #1f2937;
    color: #f3f4f6;
  }
}
</style>
